import { deriveExperienceInsightsFromValues } from '@/core/live-insights'
import type { PipData } from '@/services/pip-service'
import type { AppLocale } from '@/i18n/messages'
import { normalizePipSkin, normalizePipStreamOptions, type PipSkin, type PipStreamOptions } from './pip-skins'
import type { StreamHudData } from './stream-hud'
import { compact, duration, estimate, liveRate, percent, signedCompact, statusText } from './widget-format'
import { formatExpRateTrend } from './exp-rate-trend'
import { presentExpReadout } from './exp-readout-presentation'

/** Shared display values for everyday and stream skins in the PiP window. */
export interface WidgetSnapshot {
  version: 1
  skin: PipSkin
  options: PipStreamOptions
  locale: AppLocale
  theme: 'light' | 'dark'
  data: StreamHudData
  mesosNetTone: -1 | 0 | 1
  mesosBalanceTone: -1 | 0 | 1
  unresolvedExp: boolean
}

export function buildWidgetSnapshot(input: PipData, locale: AppLocale, theme: 'light' | 'dark'): WidgetSnapshot {
  const { session, state, liveRates, isStarting, isEnding } = input
  const en = locale === 'en'
  const ratio = (value: number | null | undefined) => typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0
  const tone = (value: number | null | undefined): -1 | 0 | 1 => !value || !Number.isFinite(value) ? 0 : value > 0 ? 1 : -1
  const pendingExp = liveRates?.expPerHour.calculationStatus === 'pending-transition'
  const unresolvedExp = pendingExp || liveRates?.expPerHour.calculationStatus === 'unresolved-level'
  const readout = liveRates?.expReadout
  const recovery = readout?.scope === 'recovery'
  const held = readout?.status === 'updating'
  const hidden = readout ? readout.status === 'stale' || readout.status === 'recovering' : unresolvedExp
  const rate = hidden ? null : held ? readout?.lastConfirmed?.value ?? null
    : recovery ? liveRates?.expRecovery?.value ?? null
      : liveRates?.expPerHour.status !== 'unavailable' ? liveRates?.expPerHour.value ?? null : null
  const point = readout?.lastConfirmed?.point
  const experience = deriveExperienceInsightsFromValues(readout ? point?.requiredExp : state?.exp.requiredExp,
    readout ? point?.absolute : state?.exp.absolute, rate)
  const status = isEnding ? 'ending' : isStarting ? 'starting' : session?.status ?? 'idle'
  return {
    version: 1, skin: normalizePipSkin(input.skin), options: normalizePipStreamOptions(input.streamOptions), locale, theme,
    mesosNetTone: tone(session?.aggregate.mesosDelta), mesosBalanceTone: tone(session?.aggregate.mesosBalanceChange), unresolvedExp,
    data: {
      sessionId: session?.id ?? null, status,
      statusText: isEnding ? (en ? 'Finishing' : '正在结束') : isStarting ? (en ? 'Confirming' : '确认中') : statusText(session?.status, locale),
      elapsed: duration(session?.activeDurationMs ?? 0), level: state?.level == null ? '--' : String(state.level),
      expRate: compact(rate, locale),
      expReadout: presentExpReadout(liveRates, session, locale),
      expRateValue: held || hidden ? null : rate,
      expRateTrend: formatExpRateTrend(liveRates?.expRealtime, liveRates?.expPerHour.value, locale, status === 'running' && !unresolvedExp && !recovery && !held && !hidden),
      expPercent: percent(state?.exp.ratio, 2), expRatio: ratio(state?.exp.ratio), eta: estimate(experience.etaMs, locale),
      expGained: hidden || recovery ? '--' : signedCompact(held ? readout?.lastConfirmed?.netGain : session?.aggregate.expGained, locale),
      expGainedLabel: (session?.sessionAccountingVersion ?? 9) >= 9 ? (en ? 'Net EXP' : '净') : (en ? 'Gained' : '本次'),
      fullDuration: estimate(experience.fullLevelDurationMs, locale),
      mesosRate: liveRate(liveRates?.mesosPerHour, locale), mesosNet: signedCompact(session?.aggregate.mesosDelta, locale),
      mesosStart: compact(session?.aggregate.mesosStartBalance, locale), mesosBalanceChange: signedCompact(session?.aggregate.mesosBalanceChange, locale),
      hpPercent: percent(state?.hp.ratio), hpRatio: ratio(state?.hp.ratio),
      mpPercent: percent(state?.mp.ratio), mpRatio: ratio(state?.mp.ratio),
      map: input.enabledMetrics?.mapName === false ? '' : state?.mapName ?? (en ? 'Recognizing map' : '地图识别中'),
      enabled: { ...(input.enabledMetrics ?? { mesos: true, hp: true, mp: true, mapName: true }) },
      ...(input.levelUpId ? { levelUpId: input.levelUpId } : {}),
    },
  }
}
